import { MeetingRoomPurposeCode } from 'domain/feature/hub/company/meeting-room/manage-meeting-room/api/constants'
import { IPublicFormSettings } from './api/settingsMappers'
import { IPublicMeetingRoom } from './types'

export interface IPurposeReservationRules {
  spacesLimit: number
  daysLimit: number
  minLeadTime: number
  maxDurationMinutes: number | null
  eventInterestDemonstration: boolean
}

const DEFAULT_SPACES_LIMIT = 1
const DEFAULT_DAYS_LIMIT = 1
const DEFAULT_MIN_LEAD_TIME = 0

const toPositiveInt = (value: number | undefined | null, fallback: number): number => {
  if (value == null || !Number.isFinite(value) || value < 1) return fallback
  return Math.floor(value)
}

export const isEventPurpose = (purposeCode: number): boolean =>
  purposeCode === MeetingRoomPurposeCode.Event

export const isSamePurposeGroup = (a: number, b: number): boolean => a === b

export const selectionUsesEventRules = (rooms: IPublicMeetingRoom[]): boolean =>
  rooms.some(room => isEventPurpose(room.purposeCode))

export const resolvePurposeRules = (
  rooms: IPublicMeetingRoom[],
  settings: IPublicFormSettings | null
): IPurposeReservationRules => {
  const useEventRules = selectionUsesEventRules(rooms)

  if (settings == null) {
    return {
      spacesLimit: DEFAULT_SPACES_LIMIT,
      daysLimit: DEFAULT_DAYS_LIMIT,
      minLeadTime: DEFAULT_MIN_LEAD_TIME,
      maxDurationMinutes: null,
      eventInterestDemonstration: false,
    }
  }

  if (useEventRules) {
    return {
      spacesLimit: toPositiveInt(settings.eventSpacesLimit, DEFAULT_SPACES_LIMIT),
      daysLimit: toPositiveInt(settings.eventDaysLimit, DEFAULT_DAYS_LIMIT),
      minLeadTime: Math.max(0, Math.floor(Number(settings.eventMinLeadTime) || 0)),
      maxDurationMinutes: null,
      eventInterestDemonstration: settings.eventInterestDemonstration === true,
    }
  }

  const maxDuration = settings.meetingMaxDuration
  return {
    spacesLimit: toPositiveInt(settings.meetingSpacesLimit, DEFAULT_SPACES_LIMIT),
    daysLimit: toPositiveInt(settings.meetingDaysLimit, DEFAULT_DAYS_LIMIT),
    minLeadTime: Math.max(0, Math.floor(Number(settings.meetingMinLeadTime) || 0)),
    maxDurationMinutes:
      maxDuration != null && Number.isFinite(maxDuration) && maxDuration > 0
        ? Math.floor(maxDuration)
        : null,
    eventInterestDemonstration: false,
  }
}

export const getSpacesLimitForPurpose = (
  purposeCode: number,
  settings: IPublicFormSettings | null
): number => {
  if (settings == null) return DEFAULT_SPACES_LIMIT
  if (isEventPurpose(purposeCode)) {
    return toPositiveInt(settings.eventSpacesLimit, DEFAULT_SPACES_LIMIT)
  }
  return toPositiveInt(settings.meetingSpacesLimit, DEFAULT_SPACES_LIMIT)
}

export const getMaxSpacesWhenEmpty = (settings: IPublicFormSettings | null): number => {
  if (settings == null) return DEFAULT_SPACES_LIMIT
  return Math.max(
    toPositiveInt(settings.meetingSpacesLimit, DEFAULT_SPACES_LIMIT),
    toPositiveInt(settings.eventSpacesLimit, DEFAULT_SPACES_LIMIT)
  )
}

export const canSelectRoom = (
  room: IPublicMeetingRoom,
  selectedRooms: IPublicMeetingRoom[],
  settings: IPublicFormSettings | null
): boolean => {
  if (selectedRooms.some(selected => selected.id === room.id)) return true
  if (selectedRooms.length === 0) return true

  const selectedPurpose = selectedRooms[0].purposeCode
  if (!isSamePurposeGroup(selectedPurpose, room.purposeCode)) return false

  const spacesLimit = getSpacesLimitForPurpose(selectedPurpose, settings)
  return selectedRooms.length < spacesLimit
}
